import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SEOHead } from '../components/SEOHead';
import { useAuth } from '@/core/AuthContext';

/**
 * Landing page for trainers
 *
 * Key messaging:
 * - AI-powered workouts from YOUR exercise library
 * - Phone call reminders for client accountability
 * - Automated client progress tracking
 */
export function TrainerLanding() {
  const navigate = useNavigate();
  const { user, userData, loading } = useAuth();

  // Redirect signed-in users to their dashboard
  useEffect(() => {
    if (loading) return;
    
    if (user && userData) {
      if (userData.role === 'trainer') {
        navigate('/trainer', { replace: true });
      } else {
        navigate('/client', { replace: true });
      }
    }
  }, [user, userData, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Don't render if user is signed in (will redirect)
  if (user && userData) {
    return null;
  }

  const features = [
    {
      icon: '🤖',
      title: 'AI generates workouts from YOUR exercise library',
      description: 'Your custom exercises, powered by AI. Create personalized workout programs at scale without sacrificing your training methodology.',
    },
    {
      icon: '📞',
      title: 'Phone call reminders keep clients accountable',
      description: 'Automated phone calls remind your clients before each workout. Higher attendance means better results and happier clients.',
    },
    {
      icon: '📊',
      title: 'Track client progress automatically',
      description: 'Automatic progress tracking and analytics. Spend less time on admin work and more time training.',
    },
  ];

  return (
    <>
      <SEOHead
        title="Grow Your Training Business with AI | Start Grounded"
        description="AI-powered workouts, phone call reminders, automated client progress tracking. Scale your personal training business."
        canonicalUrl="https://startgrounded.com/trainers"
        ogImage="https://startgrounded.com/og-image.png"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-900/80 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 safe-top">
          <div className="flex justify-between items-center">
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl font-bold bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent"
            >
              Start Grounded
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Button variant="primary" onClick={() => navigate('/trainer-auth')}>
                Get Started
              </Button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6"
            >
              Grow Your{' '}
              <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent">
                Training Business
              </span>
              <br />
              with AI
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl sm:text-2xl text-gray-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto"
            >
              AI-powered workouts, phone call reminders, automated client progress tracking
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                size="lg"
                variant="primary"
                onClick={() => navigate('/trainer-auth')}
                className="text-lg px-8 py-4 shadow-2xl shadow-primary-500/30 hover:shadow-primary-500/50 transition-all"
              >
                <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Get Started
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-primary-500/10 to-accent-500/10 dark:from-primary-500/5 dark:to-accent-500/5 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card variant="elevated" padding="lg" className="h-full hover:shadow-2xl transition-shadow">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Scale Your Training Business?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join Start Grounded today and spend more time training, less time on admin work.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate('/trainer-auth')}
              className="text-lg px-10 py-5 shadow-2xl hover:shadow-white/30 transition-all"
            >
              Get Started
              <svg className="w-6 h-6 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h4 className="text-xl font-bold bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent mb-4">
              Start Grounded
            </h4>
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              Train smarter, move better, never alone.
            </p>
            <div className="flex justify-center gap-6 mb-4">
              <a href="/privacy" className="text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-gray-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400">
                Terms of Service
              </a>
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-500">
              © 2026 Start Grounded. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
